import { OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import {
  GroupDataPoint,
  DataPoint,
} from '../../../../core/types/data-point.js';
import { getGroupedDataPointsByCoordinate } from '../../../../core/utils/group-by-threshold.js';
import { DimensionXYPoint } from '../../../types/categorical-bar-chart-data.js';
import { Layout } from '../../../types/categorical-bar-chart.js';
import { isVertical } from '../../../utils/layout.js';

/**
 * Maps a dimension object to a DataPoint object with modified properties.
 * @param dimension - The dimension object to be mapped.
 * @returns The mapped DataPpoint object.
 */
export const mapToDataPoint = (dimension: DimensionXYPoint): DataPoint => ({
  ...dimension,
  x: dimension.x + OVERLAY_PADDING,
  y: dimension.y + OVERLAY_PADDING,
  color: dimension.datapoint.color,
  id: dimension.datapoint.id,
});

/**
 * Filters the data points that would fall outside the charts, but inside the overlays area
 * @param chartLimit -
 * @param coordinate -
 */
export const filterOutOfChartBounds =
  (chartLimit: number, coordinate: 'x' | 'y') =>
  (dimension: DataPoint): boolean =>
    dimension[coordinate] >= OVERLAY_PADDING &&
    dimension[coordinate] <= chartLimit + OVERLAY_PADDING;

/**
 * Get groupDataPoints related to the dimension id provided by parameter
 *
 * @param groupDataPoints - groupDataPoints to search by
 * @param dimensionId - search by id
 * @returns groupDataPoint related to dimensionId
 */
export function getGroupDataPointsBy(
  groupDataPoints: GroupDataPoint,
  dimensionId: string,
): GroupDataPoint {
  return groupDataPoints?.filter((points) =>
    points.some((d) => d.id === dimensionId),
  );
}

/**
 * Generate groupDataPoints base on DimensionXYPoint provided by param.
 * @param points - dimensionXYPoints that will be use to generate the group data points.
 * @param isGroupAllowed - toggle to allow group functionality. wont be necessary for all cases.
 * @param layout - layout orientation of the categorical bar chart
 * @param chartLimit - chart limit, which is the chart height for vertical orientation and chart width for horizontal orientation
 * @returns groupDataPoint
 */
export function generateGroupDataPoints(
  points: DimensionXYPoint[],
  isGroupAllowed: boolean,
  layout: Layout,
  chartLimit: number,
): GroupDataPoint {
  const coordinate = isVertical(layout) ? 'y' : 'x';
  const mappedPoints = points
    .map(mapToDataPoint)
    .filter(filterOutOfChartBounds(chartLimit, coordinate));

  if (!isGroupAllowed) {
    return mappedPoints.map((point) => [point]);
  }

  return getGroupedDataPointsByCoordinate(mappedPoints, coordinate);
}
